import { useMemo } from "react";
import { useGetEntitlementsQuery } from "../store/api/postgresUserApi";
import { useAuth } from "../supabase/AuthProvider";
import { Feature, type PlanTier } from "../types/newtypes";
import { PLAN_TIERS, normalizePlanTier } from "@pedagogue/entitlements-types";

/**
 * Hook for accessing user entitlements based on their organisation's plan tier.
 * Provides easy access to check features and plan information.
 */
export const useEntitlements = () => {
  const { user } = useAuth();

  const {
    data: entitlements,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetEntitlementsQuery(undefined, {
    skip: !user, // Only fetch if user is logged in
  });

  /**
   * Check if user has access to a specific feature
   */
  const hasFeature = useMemo(() => {
    return (feature: Feature): boolean => {
      if (!entitlements) {
        return false;
      }
      if (entitlements.isSubscriptionExpired) {
        return false;
      }
      return entitlements.features.includes(feature);
    };
  }, [entitlements]);

  /**
   * Check if user has any of the specified features
   */
  const hasAnyFeature = useMemo(() => {
    return (...features: Feature[]): boolean => {
      if (!entitlements) {
        return false;
      }
      if (entitlements.isSubscriptionExpired) {
        return false;
      }
      return features.some((feature) =>
        entitlements.features.includes(feature),
      );
    };
  }, [entitlements]);

  /**
   * Check if user has all of the specified features
   */
  const hasAllFeatures = useMemo(() => {
    return (...features: Feature[]): boolean => {
      if (!entitlements) {
        return false;
      }
      if (entitlements.isSubscriptionExpired) {
        return false;
      }
      return features.every((feature) =>
        entitlements.features.includes(feature),
      );
    };
  }, [entitlements]);

  /**
   * Get the current plan tier
   */
  const planTier: PlanTier | null = entitlements?.planTier ?? null;

  /**
   * Check if user is on a specific plan tier
   */
  const isPlanTier = useMemo(() => {
    return (tier: PlanTier): boolean => {
      return planTier === tier;
    };
  }, [planTier]);

  /**
   * Check if user is on a paid school band.
   */
  const canonicalPlanTier = normalizePlanTier(planTier);
  const isPremium =
    canonicalPlanTier === PLAN_TIERS.WHOLE_SCHOOL ||
    canonicalPlanTier === PLAN_TIERS.ENTERPRISE_SCHOOL;

  /**
   * Check if subscription is expired
   */
  const isSubscriptionExpired = entitlements?.isSubscriptionExpired ?? false;

  /**
   * Get subscription expiry date
   */
  const subscriptionValidUntil = entitlements?.subscriptionValidUntil ?? null;

  return {
    // Raw data
    entitlements,
    isLoading,
    isError,
    error,
    refetch,

    // Plan info
    planTier,
    isPremium,
    isPlanTier,
    isSubscriptionExpired,
    subscriptionValidUntil,

    // Feature checks
    hasFeature,
    hasAnyFeature,
    hasAllFeatures,
  };
};

/**
 * Type-safe feature constants for use with useEntitlements
 */
export const Features = {
  COURSE_ACCESS: Feature.COURSE_ACCESS,
  ADMIN_CONSOLE: Feature.ADMIN_CONSOLE,
  COMPLIANCE_EXPORTS: Feature.COMPLIANCE_EXPORTS,
  COURSE_CERTIFICATES: Feature.COURSE_CERTIFICATES,
  PASSPORT_STAMPS: Feature.PASSPORT_STAMPS,
  PEDAGOGUE_CERTIFICATES: Feature.PEDAGOGUE_CERTIFICATES,
  ADVANCED_ANALYTICS: Feature.ADVANCED_ANALYTICS,
  AT_RISK_REPORTS: Feature.AT_RISK_REPORTS,
  PEDA_LIVE_TUTOR: Feature.PEDA_LIVE_TUTOR,
  PEDA_LIVE_SUPERVISOR: Feature.PEDA_LIVE_SUPERVISOR,
  PEDA_LIVE_CROSS_PRODUCT_SIGNALS: Feature.PEDA_LIVE_CROSS_PRODUCT_SIGNALS,
  PEDA_LIVE_SUPERVISOR_LLM_EXPLAIN: Feature.PEDA_LIVE_SUPERVISOR_LLM_EXPLAIN,
  PEDA_LIVE_TUTOR_LLM_ASSIST: Feature.PEDA_LIVE_TUTOR_LLM_ASSIST,
  GOVERNOR_PACK_PDF: Feature.GOVERNOR_PACK_PDF,
  AI_COMPETENCY_METRICS: Feature.AI_COMPETENCY_METRICS,
  BULK_INVITE: Feature.BULK_INVITE,
  // Resource Library flag (2026-07-15 pricing lock; in-app surface ships 1 Sep).
  // Ordinal position mirrors the shared enum (before PLAY_SANDBOX) — the
  // useEntitlements.test.ts equality against ALL_FEATURES is order-sensitive.
  RESOURCE_LIBRARY: Feature.RESOURCE_LIBRARY,
  COMMUNITY: Feature.COMMUNITY,
  PROFILE_PHOTOS: Feature.PROFILE_PHOTOS,
  PLAY_SANDBOX: Feature.PLAY_SANDBOX,
  WORKBOOKS: Feature.WORKBOOKS,
  AI_WORKBOOKS: Feature.AI_WORKBOOKS,

  AI_ANALYTICS: Feature.AI_ANALYTICS,
  STAFF_SURVEYS: Feature.STAFF_SURVEYS,
} as const;

export default useEntitlements;
